import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEffect, useMemo, useState } from "react";
import { uploadToBucket } from "@/lib/storage";
import { toast } from "sonner";

const SellerProductEdit = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: product } = useQuery({
    queryKey: ["edit-product", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, shop_id, title, description, price_cents, currency, stock, status")
        .eq("id", id)
        .maybeSingle();
      return data;
    },
    enabled: !!id,
  });

  const { data: categories } = useQuery({
    queryKey: ["all-categories"],
    queryFn: async () => {
      const { data } = await supabase.from("categories").select("id, name").order("name");
      return data ?? [];
    },
  });

  const { data: linkedCats } = useQuery({
    queryKey: ["product-categories", id],
    queryFn: async () => {
      if (!id) return [] as any[];
      const { data } = await supabase
        .from("product_categories")
        .select("category_id")
        .eq("product_id", id);
      return data ?? [];
    },
    enabled: !!id,
  });

  const initialCategoryId = useMemo(() => linkedCats?.[0]?.category_id ?? "", [linkedCats]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    price_cents: 0,
    currency: "BSD",
    stock: 0,
    status: "active",
    category_id: "",
  });

  useEffect(() => {
    if (product) {
      setForm({
        title: product.title ?? "",
        description: product.description ?? "",
        price_cents: product.price_cents ?? 0,
        currency: product.currency ?? "BSD",
        stock: product.stock ?? 0,
        status: product.status ?? "active",
        category_id: initialCategoryId,
      });
    }
  }, [product, initialCategoryId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!product) throw new Error("No product");
      const { error } = await supabase
        .from("products")
        .update({
          title: form.title,
          description: form.description,
          price_cents: Math.round(Number(form.price_cents)),
          currency: form.currency,
          stock: Number(form.stock),
          status: form.status,
        })
        .eq("id", product.id);
      if (error) throw error;
      // Update product_categories (single selection for now)
      if (form.category_id) {
        // Upsert by deleting existing then inserting selected
        await supabase.from("product_categories").delete().eq("product_id", product.id);
        await supabase
          .from("product_categories")
          .insert({ product_id: product.id, category_id: form.category_id })
          .catch((e) => console.warn("category link err", e));
      } else {
        await supabase.from("product_categories").delete().eq("product_id", product.id);
      }
    },
    onSuccess: () => {
      toast.success("Product updated");
      qc.invalidateQueries({ queryKey: ["edit-product", id] });
      qc.invalidateQueries({ queryKey: ["my-products"] });
    },
    onError: (e: any) => toast.error(e.message || "Failed to update"),
  });

  const { data: images } = useQuery({
    queryKey: ["product-images", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("product_images")
        .select("id, url, sort")
        .eq("product_id", id)
        .order("sort", { ascending: true });
      return data ?? [];
    },
    enabled: !!id,
  });

  const uploadImage = async (file?: File | null) => {
    if (!file || !id) return;
    const ext = file.name.split(".").pop() || "jpg";
    const path = `products/${id}/${Date.now()}.${ext}`;
    const url = await uploadToBucket({ bucket: "product_images", path, file });
    await supabase.from("product_images").insert({ product_id: id, url, sort: (images?.length ?? 0) });
    toast.success("Image uploaded");
    qc.invalidateQueries({ queryKey: ["product-images", id] });
  };

  const deleteImage = useMutation({
    mutationFn: async (imageId: string) => {
      await supabase.from("product_images").delete().eq("id", imageId);
    },
    onSuccess: () => {
      toast.success("Image removed");
      qc.invalidateQueries({ queryKey: ["product-images", id] });
    },
    onError: (e: any) => toast.error(e.message || "Failed to remove image"),
  });

  if (!product) {
    return (
      <main className="container mx-auto px-4 py-10">
        <h1 className="text-3xl font-heading font-bold mb-3">Edit Product</h1>
        <p className="text-muted-foreground">Loading product...</p>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-heading font-bold">Edit Product</h1>
        <Button variant="outline" onClick={() => navigate("/seller/products")}>Back</Button>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        <div className="rounded-xl border p-6 grid gap-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Price (cents)</Label>
              <Input type="number" value={form.price_cents} onChange={(e) => setForm((f) => ({ ...f, price_cents: Number(e.target.value) }))} />
            </div>
            <div>
              <Label>Stock</Label>
              <Input type="number" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: Number(e.target.value) }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <select className="w-full border rounded px-3 py-2 bg-background" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div>
              <Label>Category</Label>
              <select className="w-full border rounded px-3 py-2 bg-background" value={form.category_id} onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}>
                <option value="">No category</option>
                {(categories ?? []).map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-2">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>Save Changes</Button>
          </div>
        </div>
        <div className="rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Images</h2>
            <label className="text-sm cursor-pointer font-medium">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0] ?? null)} />
              <span className="inline-block px-3 py-2 border rounded hover:bg-accent/20">Upload</span>
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {(images ?? []).map((img: any) => (
              <div key={img.id} className="relative group">
                <img src={img.url} alt="Product" className="w-full h-32 object-cover rounded border" />
                <button
                  className="absolute top-1 right-1 text-xs bg-background/80 border rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition"
                  onClick={() => deleteImage.mutate(img.id)}
                >
                  Remove
                </button>
              </div>
            ))}
            {!images?.length && <div className="text-sm text-muted-foreground">No images yet.</div>}
          </div>
        </div>
      </div>
    </main>
  );
};

export default SellerProductEdit;
